import {
    useQuery,
    UseQueryOptions,
    UseQueryResult,
} from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { useSelector } from 'react-redux'

import { getAsyncUpdates } from 'api/Cerebro/orgScoped/updatesApi'
import { CerebroPaginatedResponse } from 'api/Cerebro/types'
import { useOrgContext } from 'context/OrgContext'
import { formatPagination, formatSorter } from 'helpers/params'
import { useCerebroApiRequest } from 'hooks'
import { CobaltPath } from 'routing/Path'
import { selectTableSettings } from 'selectors/ui'
import { AsyncUpdate, RootReduxState } from 'types'

export const BULK_UPDATES_QUERY_PREFIX = 'bulkUpdates'

interface BulkUpdatesTableDataQueryProps {
    tablePath: CobaltPath
    options?: Omit<
        UseQueryOptions<
            CerebroPaginatedResponse<AsyncUpdate> | null,
            AxiosError
        >,
        'queryKey' | 'queryFn'
    >
}

export const useBulkUpdatesTableDataQuery = ({
    tablePath,
    options,
}: BulkUpdatesTableDataQueryProps): UseQueryResult<
    CerebroPaginatedResponse<AsyncUpdate> | null,
    AxiosError
> => {
    const makeCerebroApiRequest = useCerebroApiRequest({ throwOnError: true })
    const { orgId } = useOrgContext()

    const { pagination, sorter } = useSelector((state: RootReduxState) =>
        selectTableSettings(state, tablePath)
    )

    const params: Record<string, unknown> = {
        ...formatPagination(pagination),
        ...formatSorter(sorter),
    }

    const queryKey = [
        BULK_UPDATES_QUERY_PREFIX,
        orgId,
        tablePath,
        { ...params },
    ]

    return useQuery({
        queryKey,
        queryFn: () =>
            makeCerebroApiRequest({ request: getAsyncUpdates(params) }),
        ...options,
    })
}
